// ============================================================================
// SESSION WARNING MODAL
// Modal de aviso de expiración de sesión con countdown
// ============================================================================
import { Clock, LogOut, RefreshCw } from 'lucide-react';

interface SessionWarningModalProps {
  isVisible: boolean;
  secondsLeft: number;
  onContinue: () => Promise<void>;
  onLogout: () => void;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SessionWarningModal({
  isVisible,
  secondsLeft,
  onContinue,
  onLogout,
}: SessionWarningModalProps) {
  if (!isVisible) return null;

  const isUrgent = secondsLeft <= 30;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all">

          {/* Header */}
          <div className="flex items-start gap-4 p-6 border-b border-gray-100">
            <div className={`p-2 rounded-lg ${isUrgent ? 'bg-red-100' : 'bg-amber-100'}`}>
              <Clock className={`h-5 w-5 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Sesión a punto de expirar
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Tu sesión se cerrará automáticamente por inactividad.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full mb-4 ${
              isUrgent ? 'bg-red-50' : 'bg-amber-50'
            }`}>
              <span className={`text-3xl font-bold tabular-nums ${
                isUrgent ? 'text-red-600' : 'text-amber-600'
              }`}>
                {formatCountdown(secondsLeft)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              ¿Deseas continuar con tu sesión?
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-100">
            <button
              type="button"
              onClick={onLogout}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center gap-2 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
            <button
              type="button"
              onClick={onContinue}
              className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl flex items-center gap-2 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Continuar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SessionWarningModal;
